import { ref } from 'vue';
import type { ContactGroups } from '@/modules/settings/interfaces/ContactGroups';
import { storeToRefs } from 'pinia';
import clientsApi from '@/api/clients-api';

import { useContactGroupsStore } from '@/modules/settings/stores/contactGroups';
import { CONTACTGROUPS } from '@/api/url';

const useContactGroups = () => {
    const storeContactGroups = useContactGroupsStore();
    const { contactGroups, contacts, currentGroup } = storeToRefs(storeContactGroups);
    const mounting = ref(true);

    const handleOrderGroup = (data: ContactGroups[]) => {
        data.sort(function (a, b) {
            if (a.contact_group > b.contact_group) return 1;
            if (a.contact_group < b.contact_group) return -1;
            return 0;
        });
    };

    const getContactGroups = async (): Promise<ContactGroups[]> => {
        const { data } = await clientsApi.get(CONTACTGROUPS);

        if (data.contact_groups.length && !contactGroups.value.length) {
            handleOrderGroup(data.contact_groups);

            if (!contacts.value.length) getContacts(data.contact_groups[0]?.contact_group);
        }

        storeContactGroups.setContactGroups(data.contact_groups);
        mounting.value = false;
        return data;
    };

    const getContacts = async (name: string): Promise<ContactGroups[]> => {
        const { data } = await clientsApi.get(`${CONTACTGROUPS}details/?name=${name}`);
        const a = data.contacts.map((a: { id: string; address: string; domain: string }) => {
            if (a.address && a.address !== null) return { id: a.id, item: a.address };
            else return { id: a.id, item: a.domain };
        });
        storeContactGroups.setContact(a);
        return data;
    };

    const addContactGroup = async (data: { name: string; mailboxes: string[] }): Promise<void> => {
        const b = data.mailboxes.map((a) => {
            if (a.includes('@')) return { domain: a.slice(a.indexOf('@') + 1), address: a };
            return { domain: a, address: null };
        });

        await clientsApi.post(CONTACTGROUPS, { name: data.name, contacts: b });
    };

    const addContacts = async (data: { name: string; mailboxes: string[] }): Promise<void> => {
        const b = data.mailboxes.map((a) => {
            if (a.includes('@')) return { domain: a.slice(a.indexOf('@') + 1), address: a };
            return { domain: a, address: null };
        });
        await clientsApi.post(`${CONTACTGROUPS}add_contacts/?name=${data.name}`, { contacts: b });
    };

    const renameContactGroup = async (data: { name: string; newName: string }): Promise<void> => {
        await clientsApi.post(`${CONTACTGROUPS}rename/?name=${data.name}`, { name: data.newName });
    };

    const removeContactGroup = async (data: { name: string }): Promise<void> => {
        await clientsApi.delete(`${CONTACTGROUPS}delete_group/?name=${data.name}`);
    };

    const removeContact = async (data: { name: string; mailboxes: string[] }): Promise<void> => {
        await clientsApi.post(`${CONTACTGROUPS}remove_contacts/?name=${data.name}`, { contact_ids: data.mailboxes });
    };

    return {
        // properties
        currentGroup,
        contactGroups,
        contacts,
        mounting,

        // Method
        getContactGroups,
        getContacts,
        addContacts,
        addContactGroup,
        renameContactGroup,
        removeContactGroup,
        removeContact
    };
};

export default useContactGroups;
